/**
 * The burning ring: the weapon of a character that has no weapon.
 *
 * Holding the attack raises a ring of cold fire around the fighter. Anything of the other side
 * standing inside it burns at a steady rate for as long as the ring is up; letting go drops it at
 * once, with whatever is left still banked.
 *
 * What holds it in check is an account rather than a cooldown. The ring spends the account while
 * it is up and refills it while it is down, both at one second per second, so the player is
 * always trading now against later: burn it dry and the next three seconds are spent waiting,
 * spend half and half a recharge puts you back where you were. A cooldown would make the choice
 * for them.
 *
 * It burns and does nothing more. Its bites take health but do not stagger (D-34): a victim
 * that was knocked off its feet by every bite could not turn, strike back or walk out, and the
 * ring read as broken controls rather than as fire.
 */
import { effectDamage, horizontalDistance, hit, sightBlocked, centre, eyePosition } from './combat';
import type { TileMap } from './tilemap';
import { fightersOf, type BattleState, type Fighter, type FighterInput } from './types';

/** Seconds of fire a full account holds, and so also the wait from empty back to full. */
export const BURN_SECONDS = 3;
/**
 * The account a dropped ring needs before it can be raised again. Without it, an account scraping
 * empty would strobe the ring on and off a frame at a time: the fighter is not fighting and the
 * view is not readable, which serves nobody.
 */
export const BURN_REIGNITE = 0.35;
/** How far the ring reaches, as a multiple of the fighter's own height. */
export const BURN_REACH = 2;

/** A ring's reach in metres. Stated here so the roster, the view and the board's odds agree. */
export function burnRadius(height: number, reach = BURN_REACH): number {
  return reach * height;
}

/**
 * Raises or drops one fighter's ring for this step and burns whoever is caught in it.
 *
 * The bite is flat rather than rolled: the damage die is for blows, and a ring that flickered
 * between a quarter and double from tick to tick would read as a broken effect rather than as a
 * fighter's luck. The same reasoning already holds for the arena's hazards.
 */
export function stepBurn(
  state: BattleState,
  map: TileMap,
  f: Fighter,
  input: FighterInput,
  dt: number,
): void {
  if (f.attackType !== 'burn') return;
  const wants = input.attack && f.alive && f.stun <= 0;
  const enough = f.burning ? f.burn > 0 : f.burn >= BURN_REIGNITE;
  f.burning = wants && enough;
  if (!f.burning) {
    // The account fills while the ring is down and the attack is let go. Holding it down on an
    // account that cannot pay gathers nothing: otherwise a held attack would light the ring again
    // the moment it crossed the floor, burn straight back to empty and flicker there for ever.
    if (!wants) f.burn = Math.min(f.burnCapacity ?? BURN_SECONDS, f.burn + dt);
    // What has already burned stays counted. Zeroing it here meant that a ring raised in bursts
    // shorter than one bite never bit at all — the AI pulses it, and a player tapping the attack
    // would have found the same nothing.
    return;
  }
  f.burn = Math.max(0, f.burn - dt);
  f.burnTick += dt;
  // One bite per stated interval, however many steps that takes, so the rate does not follow the
  // step size. A long step pays every interval it crossed.
  const interval = Math.max(0.05, f.attackInterval);
  // Nudged past the boundary before dividing: fifteen steps of a thirtieth come to
  // 0.49999999999999994, and without this the half-second bite slips a step every time — five
  // bites in three seconds where the roster says six.
  const due = f.burnTick + 1e-9;
  if (due < interval) return;
  const bites = Math.floor(due / interval);
  f.burnTick -= bites * interval;
  const from = centre(f);
  for (const other of fightersOf(state)) {
    if (other === f || !other.alive || other.faction === f.faction) continue;
    if (horizontalDistance(from, centre(other)) > f.range) continue;
    // A wall between them stops it, as it stops every other weapon that reaches across the floor.
    if (sightBlocked(map, eyePosition(f), centre(other))) continue;
    const damage = effectDamage(state, other, f.damage * bites, f.id);
    if (damage > 0) hit(state, other, damage, f.id, 'ring');
  }
}
